import React, { FunctionComponent, useCallback, useMemo, useState } from 'react';
import {
  AdminApi,
  AdminInvestmentDocument,
  AdminInvestmentDocumentCreationDocumentTypeEnum,
  AdminInvestmentDocumentCreationRequest,
  DocumentsApi,
} from 'api';
import useApiCall from 'hooks/use-api-call';
import Button from 'ui/atoms/button';
import Translate from 'ui/atoms/translate';
import { Modal, ModalHeader, ModalContent, ModalFooter, ModalFooterButtons, makeModalForm } from 'ui/molecules/modal';
import Spacer from 'ui/atoms/spacer';
import * as Styled from '../add-investment-documents-modal/styled';
import Tooltip, { TooltipPosition } from 'ui/atoms/tooltip';
import Icon from 'ui/atoms/icon';
import useIsMedia, { DEVICES } from 'ui/hooks/use-is-media';
import { useTranslateWithStringArgs } from 'ui/hooks/use-translate';
import { snakeToCamelCaseWithNumbers } from 'ui/helper/case-transforms';
import { FileWithDocType } from 'ui/atoms/file-upload';
import Hint from 'src/ui/atoms/hint';
import Section from 'src/ui/atoms/section';
import FileUploadDocumentContainer from 'src/ui/atoms/file-upload/file-upload-document-container';
import FileUploadDocumentType from 'src/ui/atoms/file-upload/file-upload-document-type';

interface EditInvestmentDocumentsModalProps {
  distributionPlatformName: string;
  distributionPlatformId: string;
  documents: (AdminInvestmentDocument & { distributionPlatformName: string; distributionPlatformId: string })[];
  onClose: () => void;
  onInvestmentDocumentsEdited: () => void;
}

interface EditInvestmentDocumentsModalFields {
  distributionPlatform: string;
  documents: FileWithDocType[];
}

interface FileIndexed {
  index: number;
  document: FileWithDocType;
}

export const Form = makeModalForm<EditInvestmentDocumentsModalFields>();

const EditInvestmentDocumentsModal: FunctionComponent<EditInvestmentDocumentsModalProps> = ({
  distributionPlatformName,
  distributionPlatformId,
  onInvestmentDocumentsEdited,
  documents,
  onClose,
}) => {
  const [initialDocuments, setInitialDocuments] =
    useState<(AdminInvestmentDocument & { distributionPlatformName: string; distributionPlatformId: string })[]>(
      documents,
    );
  const [documentsUploaded, setDocumentsUploaded] = useState<FileIndexed[]>([]);
  const [isConfirmEditModalOpen, setConfirmEditModalOpen] = useState(false);
  const [error, setError] = useState('');

  const { makeAuthenticatedApi, withApi, loading } = useApiCall();

  const adminApi: AdminApi = useMemo(() => makeAuthenticatedApi(AdminApi), [makeAuthenticatedApi]);
  const documentsApi: DocumentsApi = useMemo(() => makeAuthenticatedApi(DocumentsApi), [makeAuthenticatedApi]);

  const translate = useTranslateWithStringArgs();

  const isPhone = useIsMedia(DEVICES.phone);

  const uploadFiles = useCallback(async (documents: FileWithDocType[]) => {
    let documentsRequest: AdminInvestmentDocumentCreationRequest[] = [];

    await Promise.all(
      documents.map(async (document: FileWithDocType) => {
        if (!document.documentType || !document.file) {
          documentsRequest = [];
          return documentsRequest;
        }

        const documentResponse = await documentsApi.adminDocumentsCreate({ file: document.file });
        documentsRequest.push({
          documentId: documentResponse.id,
          documentType: document.documentType as AdminInvestmentDocumentCreationDocumentTypeEnum,
        });
      }),
    );

    return documentsRequest;
  }, []);

  const validateDocuments = useCallback(() => {
    if (documentsUploaded.some((document) => !document.document.file)) {
      setError(translate('addInvestmentDocuments.errors.noDocument'));
      return;
    }

    if (documentsUploaded.some((document) => !document.document.documentType)) {
      setError(translate('addInvestment.errors.noDocumentType'));
      return;
    }

    const checkedDocumentTypes = new Set();

    const documentTypesToSubmit = [
      ...documentsUploaded.map((doc) => doc.document.documentType),
      ...initialDocuments.map((doc) => doc.documentType),
    ];

    const isDuplicateType = documentTypesToSubmit.some((documentType) => {
      if (checkedDocumentTypes.has(documentType)) {
        setError(translate('addInvestmentDocuments.errors.multipleDocsForDocType'));
        return true;
      }

      checkedDocumentTypes.add(documentType);
      return false;
    });

    if (isDuplicateType) {
      return;
    }

    openConfirmEdit();
  }, [JSON.stringify(documentsUploaded), JSON.stringify(initialDocuments), setError, translate]);

  const openConfirmEdit = useCallback(() => {
    setConfirmEditModalOpen(true);
  }, []);

  const onSubmit = useCallback(async () => {
    let documentsToSubmit: AdminInvestmentDocumentCreationRequest[] = [];
    if (documentsUploaded?.length) {
      documentsToSubmit = await uploadFiles(documentsUploaded.map((doc) => doc.document));
    }

    if (initialDocuments?.length) {
      documentsToSubmit = [
        ...documentsToSubmit,
        ...initialDocuments.map((doc) => ({
          documentId: doc.document.id,
          documentType: doc.documentType as AdminInvestmentDocumentCreationDocumentTypeEnum,
        })),
      ];
    }

    if (!documentsToSubmit?.length) return;

    withApi(async () => {
      await adminApi.adminListingsInvestmentDocumentsCreate({
        id: distributionPlatformId,
        adminBulkInvestmentDocumentCreationRequest: { documents: documentsToSubmit },
      });

      onInvestmentDocumentsEdited();
    });
  }, [JSON.stringify(documentsUploaded), JSON.stringify(initialDocuments)]);

  const addMoreDocuments = useCallback(() => {
    const lastIndex = documentsUploaded.length ? documentsUploaded[documentsUploaded.length - 1]?.index : -1;
    setDocumentsUploaded([
      ...documentsUploaded,
      { index: lastIndex + 1, document: { file: null, documentType: null } },
    ]);
  }, [JSON.stringify(documentsUploaded)]);

  const onChangeDocument = useCallback(
    (document: FileWithDocType, index: number) => {
      setError('');
      if (!document && documentsUploaded?.length === 1) {
        // remove document when only one document is left
        setDocumentsUploaded([{ index: 0, document: { file: null, documentType: null } }]);
        return;
      }
      if (!document) {
        // remove document
        setDocumentsUploaded(documentsUploaded.filter((documentObj) => documentObj.index !== index));
        return;
      }

      // change uploaded file or document type
      const filesToUpload = documentsUploaded.map((documentObj) => {
        if (documentObj.index === index) {
          return { ...documentObj, document };
        }
        return documentObj;
      });

      setDocumentsUploaded([...filesToUpload]);
    },
    [JSON.stringify(documentsUploaded)],
  );

  const onSelectDocumentType = useCallback(
    (documentType: AdminInvestmentDocumentCreationDocumentTypeEnum, documentName: string) => {
      setError('');
      const modifiedFiles = initialDocuments.map((documentObj) => {
        if (documentObj.document.name === documentName) {
          return { ...documentObj, documentType };
        }
        return documentObj;
      });

      setInitialDocuments(modifiedFiles);
    },
    [JSON.stringify(initialDocuments)],
  );

  const onRemoveDocument = useCallback(
    (documentToRemove: string) => {
      withApi(async () => {
        // remove existing document
        setInitialDocuments(initialDocuments.filter((document) => document.document.name !== documentToRemove));
      });
    },
    [JSON.stringify(initialDocuments)],
  );

  const getDocumentTypesOptions = useCallback(() => {
    return Object.values(AdminInvestmentDocumentCreationDocumentTypeEnum).map((documentType) => ({
      label: translate(`investmentDocumentTypes.${snakeToCamelCaseWithNumbers(documentType)}`),
      value: documentType,
    }));
  }, [translate, AdminInvestmentDocumentCreationDocumentTypeEnum]);

  return (
    <Modal onClose={onClose}>
      <Form
        i18nKey="addInvestmentDocuments"
        onSubmit={() => {
          isConfirmEditModalOpen ? onSubmit() : validateDocuments();
        }}
        initial={{
          distributionPlatform: distributionPlatformName,
        }}
      >
        <ModalHeader>
          <Translate name="editInvestmentDocuments.title" />
        </ModalHeader>
        <ModalContent>
          {isConfirmEditModalOpen && (
            <>
              <Section>
                <Translate name="editInvestmentDocuments.confirmEdit.content" />
              </Section>
              <Hint variant="warning">
                <Translate name="editInvestmentDocuments.confirmEdit.hint" />
              </Hint>
            </>
          )}
          {!isConfirmEditModalOpen && (
            <>
              <Form.Group name="distributionPlatform" required={true}>
                <Form.Select
                  options={[
                    {
                      value: distributionPlatformName,
                      label: distributionPlatformName,
                    },
                  ]}
                  disabled
                  portalTarget={document.body}
                ></Form.Select>
              </Form.Group>
              <Styled.SameRowWithTooltip>
                <Styled.LabelTooltip>
                  <Translate name="addInvestmentDocuments.investmentDocuments" />
                </Styled.LabelTooltip>
                <Tooltip
                  content={<Translate name="addInvestmentDocuments.tooltip" />}
                  position={isPhone ? TooltipPosition.BOTTOM : TooltipPosition.RIGHT}
                  maxWidth={200}
                >
                  <Icon name="question" circular={true} size="large" color="info" />
                </Tooltip>
                <Spacer y={2} />
              </Styled.SameRowWithTooltip>
              {initialDocuments.map((document) => (
                <>
                  <FileUploadDocumentContainer
                    uploadedFile={{
                      file: {
                        name: document.document.name,
                      } as File,
                    }}
                    onRemoveDocument={onRemoveDocument}
                  />
                  <FileUploadDocumentType
                    onSelectDocumentType={onSelectDocumentType}
                    uploadedFile={{
                      file: {
                        name: document.document.name,
                      } as File,
                      documentType: document.documentType,
                    }}
                    documentTypes={getDocumentTypesOptions()}
                  />
                </>
              ))}
              <Form.Group name="documents" label={null}>
                {documentsUploaded &&
                  documentsUploaded.map((documentUploaded) => {
                    return (
                      <Form.FileUpload
                        key={documentUploaded.index}
                        onChange={(document: FileWithDocType) => onChangeDocument(document, documentUploaded.index)}
                        documentTypes={getDocumentTypesOptions()}
                      />
                    );
                  })}
                {Form.Validators.FileSize(5000000)}
              </Form.Group>
              {!!error?.length && (
                <Hint variant="danger">
                  <Translate name={error} />
                </Hint>
              )}
              <Button variant="link" icon="plus" size="small" iconSize="small" onClick={addMoreDocuments}>
                <Translate name="addInvestmentDocuments.addFurtherDocuments" />
              </Button>
            </>
          )}
        </ModalContent>
        <ModalFooter>
          <ModalFooterButtons
            actionButtons={[
              {
                name: 'cancel',
                content: <Translate name="common.close" />,
                size: 'large',
                onClick: onClose,
              },
              {
                name: 'save',
                content: <Translate name="common.submit" />,
                variant: 'primary',
                type: 'submit',
                size: 'large',
                disabled: !documentsUploaded?.length && !initialDocuments?.length,
                loading: loading,
              },
            ]}
          />
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default EditInvestmentDocumentsModal;
